import * as tus from "tus-js-client"

function uploadError(message = `Upload failed`) {
  return new Error(message)
}

function browserWindow() {
  return globalThis.window || null
}

function currentOrigin() {
  return (
    browserWindow()?.location?.origin || globalThis.location?.origin || null
  )
}

function uploadDebugEnabled() {
  return (
    Boolean(import.meta.env?.DEV) ||
    browserWindow()?.localStorage?.getItem(`nk:debug:uploads`) === `true`
  )
}

export function capUploadProgress(percent) {
  const normalizedPercent = Number(percent)

  if (!Number.isFinite(normalizedPercent)) {
    return null
  }

  return Math.min(Math.max(Math.round(normalizedPercent), 0), 99)
}

function requestUrl(meta) {
  return meta.upload_url || meta.url || null
}

function redactRequestUrl(url) {
  if (!url) return null

  try {
    const parsedUrl = new URL(url, currentOrigin() || `https://example.invalid`)
    return `${parsedUrl.origin}${parsedUrl.pathname}`
  } catch {
    return null
  }
}

function requestHost(meta) {
  const url = requestUrl(meta)

  if (!url) return null

  try {
    return new URL(url, currentOrigin() || `https://example.invalid`).host
  } catch {
    return null
  }
}

export function buildUploadDiagnostic(meta, file, extra = {}) {
  const detail = {
    client_error_message: meta.client_error_message || null,
    diagnostic_context: meta.diagnostic_context || null,
    failure: extra.failure || `unknown`,
    request_host: requestHost(meta),
    status: extra.status ?? null,
    upload_name: meta.upload_name || null,
    uploader: meta.uploader || null,
  }

  if (!uploadDebugEnabled()) {
    return detail
  }

  return {
    ...detail,
    file_name: file?.name || null,
    file_size: file?.size || null,
    file_type: file?.type || null,
    message: extra.message || null,
    request_url: redactRequestUrl(requestUrl(meta)),
  }
}

function emitUploadDiagnostic(detail) {
  if (uploadDebugEnabled()) {
    console.error(`External upload failed`, detail)
  }
}

function tusUploadConstructor() {
  return globalThis.__nkTusUploadConstructor || tus.Upload
}

function tusErrorMessage(error) {
  return (error instanceof Error && error.message) || uploadError().message
}

function buildTusMetadata(file, meta) {
  return {
    filename: file.name,
    filetype: file.type || `application/octet-stream`,
    ...(meta.metadata || {}),
  }
}

function tusFingerprint(file, meta) {
  return `${meta.token || `upload`}:${file.name}:${file.size}:${file.type || ``}`
}

function bunnyTusFingerprint(file, meta) {
  return [
    meta.token || `upload`,
    meta.video_id || `video`,
    file.name,
    file.size,
    file.type || ``,
  ].join(`:`)
}

function buildTusUpload(
  file,
  meta,
  { onAbort, onProgress, onSuccess, onError }
) {
  const Upload = tusUploadConstructor()

  const upload = new Upload(file, {
    endpoint: meta.upload_url,
    fingerprint: () => Promise.resolve(tusFingerprint(file, meta)),
    headers: meta.headers || {},
    metadata: buildTusMetadata(file, meta),
    removeFingerprintOnSuccess: true,
    retryDelays: [0, 3_000, 5_000, 10_000, 20_000, 60_000, 60_000],
    onError,
    onProgress,
    onSuccess,
  })

  onAbort?.(() => {
    void upload.abort()
  })

  return upload
}

export function uploadToTusd(file, meta, { onAbort, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const upload = buildTusUpload(file, meta, {
      onAbort,
      onProgress: (uploadedBytes, totalBytes) => {
        const percent = Math.round((uploadedBytes / totalBytes) * 100)
        onProgress?.(capUploadProgress(percent))
      },
      onSuccess: () => resolve({ status: 204 }),
      onError: (error) =>
        reject(
          buildUploadDiagnostic(meta, file, {
            failure: `tus_error`,
            message: tusErrorMessage(error),
          })
        ),
    })

    upload
      .findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length > 0) {
          upload.resumeFromPreviousUpload(previousUploads[0])
        }

        upload.start()
      })
      .catch((error) =>
        reject(
          buildUploadDiagnostic(meta, file, {
            failure: `resume_error`,
            message: tusErrorMessage(error),
          })
        )
      )
  })
}

export function uploadToBunnyTus(file, meta, { onAbort, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const Upload = tusUploadConstructor()
    let resumed = false

    const upload = new Upload(file, {
      endpoint: meta.upload_url,
      fingerprint: () => Promise.resolve(bunnyTusFingerprint(file, meta)),
      headers: {
        AuthorizationExpire: meta.authorization_expire,
        AuthorizationSignature: meta.authorization_signature,
        LibraryId: meta.library_id,
        VideoId: meta.video_id,
      },
      metadata: {
        filetype: file.type || `application/octet-stream`,
        title: file.name,
      },
      removeFingerprintOnSuccess: true,
      retryDelays: [0, 3_000, 5_000, 10_000, 20_000, 60_000, 60_000],
      onError: (error) =>
        reject(
          buildUploadDiagnostic(meta, file, {
            failure: `tus_error`,
            message: tusErrorMessage(error),
          })
        ),
      onProgress: (uploadedBytes, totalBytes) => {
        const percent = Math.round((uploadedBytes / totalBytes) * 100)
        onProgress?.(capUploadProgress(percent))
      },
      onSuccess: () => resolve({ resumed, status: 200 }),
    })

    onAbort?.(() => upload.abort(true))

    upload
      .findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length > 0) {
          resumed = true
          upload.resumeFromPreviousUpload(previousUploads[0])
        }

        upload.start()
      })
      .catch((error) =>
        reject(
          buildUploadDiagnostic(meta, file, {
            failure: `resume_error`,
            message: tusErrorMessage(error),
            resumed,
          })
        )
      )
  })
}

const Uploaders = {
  bunny_tus(entries, onViewError) {
    entries.forEach((entry) => {
      uploadToBunnyTus(entry.file, entry.meta, {
        onAbort: onViewError,
        onProgress: (percent) => {
          if (percent < 100) entry.progress(percent)
        },
      })
        .then(() => entry.progress(100))
        .catch((diagnostic) => {
          emitUploadDiagnostic(diagnostic)
          entry.error(`external_client_failure`)
        })
    })
  },

  s3_tusd(entries, onViewError) {
    entries.forEach((entry) => {
      uploadToTusd(entry.file, entry.meta, {
        onAbort: onViewError,
        onProgress: (percent) => {
          if (percent < 100) entry.progress(percent)
        },
      })
        .then(() => entry.progress(100))
        .catch((diagnostic) => {
          emitUploadDiagnostic(diagnostic)
          entry.error(`external_client_failure`)
        })
    })
  },
}

export default Uploaders
